import { createGlobalStyle } from "styled-components";
import "@fontsource/source-code-pro";

import "react-datepicker/dist/react-datepicker.css";
import "./core/components/Field/DatePicker/styles.css";

const GlobalStyle = createGlobalStyle`
  html, body, #root {
    height: 100%;
    overflow: hidden;
    background-color: var(--background);
  }

  html {
  }

  body {
    color: var(--text-body);
    font-family: 'Inter', sans-serif;
    -webkit-font-smoothing: subpixel-antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  * {
    box-sizing: border-box;
  }

  a {
    color: var(--text-link);
    font-size: 16px;
    text-decoration: none;

    :hover {
      text-decoration: underline;
    }
  }

  :root {
    /* Colors */
    --color-base-100: hsl(265 40% 100%);
    --color-base-200: hsl(265 2% 85%);
    --color-base-300: hsl(265 2% 35%);
    --color-base-content: oklch(0% 0 0);

    --color-primary: hsl(265 65% 20%);
    --color-accent: hsl(265 65% 95%);
    --color-secondary: hsl(265 65% 98%);

    --color-primary-content: hsl(0 0 100);
    --color-secondary-content: hsl(0 0 0);
    --color-accent-content: hsl(0 0 0);

    --color-neutral: oklch(14% 0.005 285.823);
    --color-neutral-content: oklch(92% 0.004 286.32);
    --color-info: hsl(265 65% 50%);
    --color-info-content: oklch(29% 0.066 243.157);
    --color-success: hsl(160 100% 30%);
    --color-success-content: oklch(0% 0 0);
    --color-warning: oklch(82% 0.189 84.429);
    --color-warning-content: oklch(0% 0 0);
    --color-error: oklch(55% 0.195 38.402);
    --color-error-content: oklch(100% 0 0);

    --background: var(--color-secondary);

    --icon-background: hsl(269, 24%, 47%);

    --text-header: var(--color-base-content);
    --text-body: #000;
    --text-link: var(--color-info);

    --sidebar-background: var(--color-primary);
    --sidebar-foreground: var(--color-primary-content);
    --sidebar-background-active: color-mix(in oklab, var(--sidebar-background) 70%, var(--sidebar-foreground));
    --sidebar-border: var(--sidebar-background);

    --sidebar-background-secondary: var(--color-accent);
    --sidebar-foreground-secondary:  var(--color-accent-content);
    --sidebar-background-secondary-active: color-mix(in oklab, var(--sidebar-background-secondary) 90%, var(--sidebar-foreground-secondary));
    --sidebar-secondary-border: var(--sidebar-background-secondary);

    --input-background: var(--color-base-100);
    --input-border-focus: var(--color-primary);
    --input-border: var(--color-base-300);

    --success: var(--color-success);
    --error: var(--color-error);

    --button-contained-background: hsl(270 51% 43%);
    --button-contained-foreground: var(--color-primary-content);
    --button-disabled-background: color-mix(in oklab, var(--button-contained-background) 20%, var(--background));
    --button-disabled-foreground: color-mix(in oklab, var(--button-contained-foreground) 30%, var(--background));
    /* In-flight buttons stay the neutral grey they have always shipped as. */
    --button-loading-background: #d7d7d7;

    --button-outlined-border:           ${(props) =>
      props.theme.dh_colors.purple_50};
    --button-outlined-background:       var(--color-base-100);
    --button-outlined-background-hover: ${(props) =>
      props.theme.dh_colors.purple_20};
    --button-outlined-foreground:       ${(props) =>
      props.theme.dh_colors.purple_50};

    --button-text-foreground:           ${(props) =>
      props.theme.dh_colors.purple_50};

    --button-icon-foreground:           ${(props) =>
      props.theme.dh_colors.purple_50};
    --button-icon-background-hover:     #ede6f5;

    /* Ghost is keyed by intent: with no fill or border, colour carries meaning. */
    --button-ghost-primary-foreground:       ${(props) =>
      props.theme.dh_colors.purple_50};
    --button-ghost-primary-background-hover: ${(props) =>
      props.theme.dh_colors.purple_20};
    --button-ghost-secondary-foreground:       ${(props) =>
      props.theme.dh_colors.grey_70};
    --button-ghost-secondary-background-hover: ${(props) =>
      props.theme.dh_colors.grey_20};
    --button-ghost-destructive-foreground:       ${(props) =>
      props.theme.dh_colors.salmon_70};
    --button-ghost-destructive-background-hover: ${(props) =>
      props.theme.dh_colors.salmon_20};
    --button-ghost-disabled-foreground:      ${(props) =>
      props.theme.dh_colors.grey_60};
    /* Deliberately off-family, so the focus ring reads against ghost's own colour. */
    --button-ghost-focus-outline:            ${(props) =>
      props.theme.dh_colors.blue_50};

    /* Checkbox */
    --checkbox-unchecked-bg:           ${(props) =>
      props.theme.dh_colors.white};
    --checkbox-unchecked-border:       #dddbda;
    --checkbox-unchecked-border-focus: ${(props) =>
      props.theme.dh_colors.blue_60};
    --checkbox-checked-bg:             ${(props) =>
      props.theme.dh_colors.blue_60};
    --checkbox-checked-bg-focus:       ${(props) =>
      props.theme.dh_colors.purple_80};
    --checkbox-checked-bg-disabled:    #D8D8D8;
    --checkbox-explicit-false-bg:      ${(props) =>
      props.theme.dh_colors.salmon_70};
    --checkbox-label-color:            #2b2826;
    --checkbox-tooltip-bg:             ${(props) =>
      props.theme.dh_colors.purple_90};

    /* React Tooltip Overrides */
    --rt-color-white:  ${(props) => props.theme.dh_colors.white} !important;
    --rt-color-dark: ${(props) => props.theme.dh_colors.purple_80} !important;
    --rt-opacity: 1 !important;
  }

  /* @media (prefers-color-scheme: dark) {
    :root {
      --color-base-100: hsl(265 40% 5%);
      --color-base-200: hsl(265 2% 15%);
      --color-base-300: hsl(265 10% 80%);
      --color-base-content: hsl(265 0 100%);

      --color-primary: hsl(265 65% 25%);
      --color-accent: hsl(265 65% 15%);
      --color-secondary: hsl(265 65% 5%);

      --color-primary-content: hsl(265 0 100%);
      --color-secondary-content: oklch(0% 0 0);
      --color-accent-content: hsl(265 0 100%);

      --color-neutral: oklch(14% 0.005 285.823);
      --color-neutral-content: oklch(92% 0.004 286.32);
      --color-info: hsl(265 65% 70%);
      --color-info-content: oklch(29% 0.066 243.157);
      --color-success: hsl(160 100% 35%);
      --color-success-content: oklch(0% 0 0);
      --color-warning: oklch(82% 0.189 84.429);
      --color-warning-content: oklch(0% 0 0);
      --color-error: oklch(55% 0.195 38.402);
      --color-error-content: oklch(100% 0 0);
    }
  } */

    .toast {
        --toast-bg-color: #fff;
        border-radius: 5px !important;
        color: #fff !important;

        .close-button {
            left: auto !important;
            top: -2px !important;
            right: -14px !important;
        }

        &.success {
            --toast-bg-color: var(--color-success);
            background-color: var(--toast-bg-color) !important;
            border-color: var(--toast-bg-color) !important;
        }

        &.error {
            --toast-bg-color: var(--color-error);
            background-color: var(--toast-bg-color) !important;
            border-color: var(--toast-bg-color) !important;
        }

        &.info {
            --toast-bg-color: var(--color-info);
            background-color: var(--toast-bg-color) !important;
            border-color: var(--toast-bg-color) !important;
        }
    }


`;

export default GlobalStyle;
